import { usePaymentsContext } from "../../Context/PaymentsContext";
import { usePaymentBatchApprove } from "./usePaymentBatchApprove";
import { usePaymentBatchDeny } from "./usePaymentBatchDeny";
import type {
  PrimaryButtonConfig,
  SecondaryButtonConfig,
  UsePaymentBatchReviewInput,
  UsePaymentBatchReviewResult,
} from "./types";

/**
 * Consolidated hook for payment batch review actions.
 *
 * Returns `denyModal` as a React node because the deny flow requires a modal with internal state
 * (textarea for the denial reason). By encapsulating the modal JSX within the hook, consumers
 * can simply render it without managing the modal's open/close state or the textarea value
 *
 * @param input - The batch ID and pre-computed review state
 * @returns Button configs for approve/deny and the deny modal
 */
export function usePaymentBatchReview({
  batchId,
  reviewState,
}: UsePaymentBatchReviewInput): UsePaymentBatchReviewResult {
  const { isGPX } = usePaymentsContext();
  const { handleApprove, loading: approveLoading } = usePaymentBatchApprove({
    paymentBatchId: batchId,
    isFinal: reviewState.isFinalApproval,
  });

  const {
    handleDenyClick,
    loading: denyLoading,
    denyModal,
  } = usePaymentBatchDeny({
    paymentBatchId: batchId,
  });

  const canSubmitReview = reviewState.canUserSubmitReview;

  const approveButton: PrimaryButtonConfig = {
    label: isGPX ? "Authorize" : "Approve",
    disabled: !canSubmitReview || approveLoading,
    onClick: handleApprove,
  };

  const denyButton: SecondaryButtonConfig = {
    label: "Deny",
    disabled: !canSubmitReview || denyLoading,
    onClick: handleDenyClick,
    variant: "neutral",
  };

  return {
    approveButton,
    denyButton,
    denyModal,
  };
}
